import React, { useState } from "react";
import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, TextField, InputAdornment } from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { Search, FilterList } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { HFlex } from "shared";
import {
  COLUMNS,
  DATE_PRESETS,
  STATUS_FILTER_OPTIONS,
  SITE_FILTER_OPTIONS,
  URGENCY_FILTER_OPTIONS,
  PATIENT_STATE_FILTER_OPTIONS,
  JOINT_FILTER_OPTIONS,
} from "../constants";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  searchField: {
    minWidth: 280,
  },
  filterBar: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  clearButton: {
    marginLeft: theme.spacing(1),
    textTransform: "none",
  },
  filterSelect: {
    minWidth: 160,
  },
}));

interface ReferralTrackingFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  filters: Record<string, string[]>;
  onFiltersChange: (filters: Record<string, string[]>) => void;
  dateRangeStart: string;
  dateRangeEnd: string;
  datePreset: string;
  onDatePresetChange: (preset: string) => void;
  onDateRangeChange: (start: string, end: string) => void;
}

const ReferralTrackingFilters: React.VFC<ReferralTrackingFiltersProps> = ({
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  dateRangeStart,
  dateRangeEnd,
  datePreset,
  onDatePresetChange,
  onDateRangeChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(false);

  const hasActiveFilters = Object.keys(filters).length > 0 || datePreset;

  const handleRemoveFilter = (field: string) => {
    const next = { ...filters };
    delete next[field];
    onFiltersChange(next);
  };

  const handleClearAll = () => {
    onFiltersChange({});
    onDatePresetChange("");
    onDateRangeChange("", "");
  };

  const handleDatePresetChange = (preset: string) => {
    onDatePresetChange(preset);
    if (preset !== "custom") {
      onDateRangeChange("", "");
    }
  };

  const handleSelectFilter = (field: string) => (values: string[]) => {
    if (!values || values.length === 0) {
      handleRemoveFilter(field);
      return;
    }
    onFiltersChange({ ...filters, [field]: values });
  };

  return (
    <Box className={classes.root}>
      <HFlex gap={2} alignItems="center" flexWrap="wrap">
        <TextField
          className={classes.searchField}
          placeholder={t("referral_tracking.search_placeholder")}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl variant="outlined" size="small" className={classes.filterSelect}>
          <InputLabel>{t("referral_tracking.date_range")}</InputLabel>
          <Select
            value={datePreset}
            onChange={(e) => handleDatePresetChange(e.target.value as string)}
            label={t("referral_tracking.date_range")}
          >
            {DATE_PRESETS.map((preset) => (
              <MenuItem key={preset.value} value={preset.value}>
                {preset.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {datePreset === "custom" && (
          <>
            <TextField
              type="date"
              label={t("referral_tracking.date_from")}
              value={dateRangeStart}
              onChange={(e) => onDateRangeChange(e.target.value, dateRangeEnd)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label={t("referral_tracking.date_to")}
              value={dateRangeEnd}
              onChange={(e) => onDateRangeChange(dateRangeStart, e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}
        <Button
          variant={showFilters ? "contained" : "outlined"}
          color={showFilters ? "primary" : "default"}
          startIcon={<FilterList />}
          onClick={() => setShowFilters(!showFilters)}
          size="small"
        >
          {t("referral_tracking.filters")}
          {Object.keys(filters).length > 0 && ` (${Object.keys(filters).length})`}
        </Button>
      </HFlex>

      {/* Active filter chips */}
      {hasActiveFilters && !showFilters && (
        <HFlex gap={0.5} mt={1} flexWrap="wrap" alignItems="center">
          {Object.entries(filters).map(([field, values]) => {
            const col = COLUMNS.find((c) => c.key === field);
            return (
              <Chip
                key={field}
                label={`${col?.label || field}: ${values.join(", ")}`}
                onDelete={() => handleRemoveFilter(field)}
                className={classes.chip}
                size="small"
                color="primary"
                variant="outlined"
              />
            );
          })}
          {datePreset && (
            <Chip
              label={`Date: ${DATE_PRESETS.find((p) => p.value === datePreset)?.label || datePreset}`}
              onDelete={() => {
                onDatePresetChange("");
                onDateRangeChange("", "");
              }}
              className={classes.chip}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          <Button size="small" color="primary" onClick={handleClearAll} className={classes.clearButton}>
            {t("referral_tracking.clear_all")}
          </Button>
        </HFlex>
      )}

      {/* Filter panel */}
      {showFilters && (
        <Box className={classes.filterBar}>
          <HFlex gap={2} flexWrap="wrap" alignItems="flex-end">
            <FormControl variant="outlined" size="small" className={classes.filterSelect}>
              <InputLabel>{t("referral_tracking.status_filter")}</InputLabel>
              <Select
                multiple
                value={filters.status || []}
                onChange={(e) => handleSelectFilter("status")(e.target.value as string[])}
                label={t("referral_tracking.status_filter")}
              >
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" className={classes.filterSelect}>
              <InputLabel>{t("referral_tracking.site_filter")}</InputLabel>
              <Select
                multiple
                value={filters.site || []}
                onChange={(e) => handleSelectFilter("site")(e.target.value as string[])}
                label={t("referral_tracking.site_filter")}
              >
                {SITE_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" className={classes.filterSelect}>
              <InputLabel>{t("referral_tracking.urgency_filter")}</InputLabel>
              <Select
                multiple
                value={filters.urgency || []}
                onChange={(e) => handleSelectFilter("urgency")(e.target.value as string[])}
                label={t("referral_tracking.urgency_filter")}
              >
                {URGENCY_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" className={classes.filterSelect}>
              <InputLabel>{t("referral_tracking.patient_state_filter")}</InputLabel>
              <Select
                multiple
                value={filters.patient_state || []}
                onChange={(e) => handleSelectFilter("patient_state")(e.target.value as string[])}
                label={t("referral_tracking.patient_state_filter")}
              >
                {PATIENT_STATE_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" className={classes.filterSelect}>
              <InputLabel>{t("referral_tracking.joint_filter")}</InputLabel>
              <Select
                multiple
                value={filters.invited_joint || []}
                onChange={(e) => handleSelectFilter("invited_joint")(e.target.value as string[])}
                label={t("referral_tracking.joint_filter")}
              >
                {JOINT_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </HFlex>

          {/* Active filters in panel */}
          {Object.keys(filters).length > 0 && (
            <HFlex gap={0.5} mt={1.5} pt={1.5} flexWrap="wrap" alignItems="center" borderTop={1} borderColor="divider">
              {Object.entries(filters).map(([field, values]) => {
                const col = COLUMNS.find((c) => c.key === field);
                return (
                  <Chip
                    key={field}
                    label={`${col?.label || field}: ${values.join(", ")}`}
                    onDelete={() => handleRemoveFilter(field)}
                    className={classes.chip}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                );
              })}
            </HFlex>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReferralTrackingFilters;
